import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Modal,
  TextField,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Tab,
  Tabs,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { supabase } from '../lib/supabaseClient';
import { APIProvider, Map, Marker } from '@vis.gl/react-google-maps';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight,
  faBars,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 700,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflowY: 'auto',
};

const UnifiedAddresses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Tab State (0: Adresler, 1: Gümrükler, 2: Firmalar)
  const [activeTab, setActiveTab] = useState(0);
  const [addresses, setAddresses] = useState([]);
  const [customs, setCustoms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [recordType, setRecordType] = useState('address');
  const [editingId, setEditingId] = useState(null);

  const [formData, setFormData] = useState({
    city: '',
    country: '',
    latitude: '',
    longitude: '',
    name: '',
    description: '',
    state: '',
    customs_code: '',
    customs_name: '',
    contact_person: '',
    email: '',
    phone: '',
    tax_number: '',
    company_type: 'both',
    notes: '',
    active: true
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [viewport, setViewport] = useState({
    center: { lat: 41.0082, lng: 28.9784 },
    zoom: 10,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [tableSearchQuery, setTableSearchQuery] = useState('');

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: null, type: null });

  useEffect(() => {
    fetchAddresses();
    fetchCustoms();
    fetchCompanies();
  }, []);

  const fetchAddresses = async () => {
    try {
      const { data, error } = await supabase
        .from('addresses')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAddresses(data || []);
    } catch (error) {
      console.error('Adresler hatası:', error.message);
    }
  };

  const fetchCustoms = async () => {
    try {
      const { data, error } = await supabase
        .from('customs')
        .select('*')
        .order('customs_name');
      if (error) throw error;
      setCustoms(data || []);
    } catch (error) {
      console.error('Gümrükler hatası:', error.message);
    }
  };

  const fetchCompanies = async () => {
    try {
        const { data, error } = await supabase
          .from('sender_receiver_companies')
          .select('*')
          .order('created_at', { ascending: false });
        if (error) throw error;
        setCompanies(data || []);
    } catch (error) {
        console.error('Firmalar hatası:', error.message);
    }
  };

  const handleOpenModal = (type = 'address', data = null) => {
    setRecordType(type);
    
    if (data) {
      setEditingId(data.id);
      let newForm = { ...formData };

      if (type === 'address') {
        newForm = {
            ...newForm,
            name: data.name || '',
            description: data.description || '',
            city: data.city || '',
            state: data.state || '',
            country: data.country || '',
            latitude: data.latitude || '',
            longitude: data.longitude || ''
        };
        setSearchQuery(data.description || '');
      } else if (type === 'customs') {
        newForm = {
            ...newForm,
            city: data.city || '',
            country: data.country || '',
            latitude: data.latitude || '',
            longitude: data.longitude || '',
            customs_code: data.customs_code || '',
            customs_name: data.customs_name || ''
        };
        setSearchQuery(`${data.city || ''} ${data.country || ''}`);
      } else if (type === 'company') {
        newForm = {
            ...newForm,
            name: data.name || '',
            contact_person: data.contact_person || '',
            email: data.email || '',
            phone: data.phone || '',
            description: data.address || '',
            city: data.city || '',
            country: data.country || '',
            tax_number: data.tax_number || '',
            company_type: data.company_type || 'both',
            notes: data.notes || '',
            active: data.active !== undefined ? data.active : true
        };
        setSearchQuery(data.address || '');
      }

      setFormData(newForm);
      if (data.latitude && data.longitude) {
        const lat = parseFloat(data.latitude);
        const lng = parseFloat(data.longitude);
        setSelectedLocation({ lat, lng });
        setViewport({ center: { lat, lng }, zoom: 14 });
      }
    } else {
      resetForm();
      if(activeTab === 0) setRecordType('address');
      else if(activeTab === 1) setRecordType('customs');
      else if(activeTab === 2) setRecordType('company');
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    resetForm();
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      city: '', country: '', latitude: '', longitude: '',
      name: '', description: '', state: '',
      customs_code: '', customs_name: '',
      contact_person: '', email: '', phone: '', tax_number: '',
      company_type: 'both', notes: '', active: true
    });
    setSearchQuery('');
    setSelectedLocation(null);
    setSearchResults([]);
  };

  const handleSave = async () => {
    try {
      const isEdit = !!editingId;
      let error;
      let payload = {};

      if (recordType === 'address') {
        payload = {
            name: formData.name,
            description: formData.description,
            city: formData.city,
            state: formData.state,
            country: formData.country,
            latitude: formData.latitude,
            longitude: formData.longitude
        };
        if (isEdit) {
            const { error: e } = await supabase.from('addresses').update(payload).eq('id', editingId);
            error = e;
        } else {
            const { error: e } = await supabase.from('addresses').insert([payload]);
            error = e;
        }
        if (!error) fetchAddresses();

      } else if (recordType === 'customs') {
        payload = {
            customs_code: formData.customs_code,
            customs_name: formData.customs_name,
            city: formData.city,
            country: formData.country,
            latitude: formData.latitude,
            longitude: formData.longitude
        };
        if (isEdit) {
            const { error: e } = await supabase.from('customs').update(payload).eq('id', editingId);
            error = e;
        } else {
            const { error: e } = await supabase.from('customs').insert([payload]);
            error = e;
        }
        if (!error) fetchCustoms();

      } else if (recordType === 'company') {
        payload = {
            name: formData.name,
            contact_person: formData.contact_person,
            email: formData.email,
            phone: formData.phone,
            address: formData.description,
            city: formData.city,
            country: formData.country,
            tax_number: formData.tax_number,
            company_type: formData.company_type,
            notes: formData.notes,
            active: formData.active
        };

        if (isEdit) {
            const { error: e } = await supabase.from('sender_receiver_companies').update(payload).eq('id', editingId);
            error = e;
        } else {
            const { error: e } = await supabase.from('sender_receiver_companies').insert([payload]);
            error = e;
        }
        if (!error) fetchCompanies();
      }

      if (error) throw error;
      handleCloseModal();
    } catch (error) {
      console.error('Kaydetme hatası:', error.message);
      alert('Kaydetme sırasında bir hata oluştu: ' + error.message);
    }
  };

  const confirmDelete = (id, type) => {
    setDeleteTarget({ id, type });
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    const { id, type } = deleteTarget;
    try {
        let tableName = '';
        if(type === 'address') tableName = 'addresses';
        else if(type === 'customs') tableName = 'customs';
        else if(type === 'company') tableName = 'sender_receiver_companies';

        const { error } = await supabase.from(tableName).delete().eq('id', id);
        if (error) throw error;

        if (type === 'address') fetchAddresses();
        else if (type === 'customs') fetchCustoms();
        else if (type === 'company') fetchCompanies();
        
        setShowDeleteDialog(false);
    } catch (error) {
        console.error('Silme hatası:', error.message);
    }
  };

  useEffect(() => {
    const searchLocation = async () => {
      if (!searchQuery || !openModal) {
        setSearchResults([]);
        return;
      }
      if (!window.google || !window.google.maps || !window.google.maps.places) return;

      try {
        const service = new window.google.maps.places.AutocompleteService();
        service.getPlacePredictions({ input: searchQuery }, (predictions, status) => {
            if (status === 'OK') setSearchResults(predictions);
            else setSearchResults([]);
        });
      } catch (error) {
        console.error('Autocomplete Error:', error);
      }
    };
    const timeoutId = setTimeout(searchLocation, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, openModal]);

  const handlePlaceSelect = (prediction) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) return;
    
    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.getDetails({ placeId: prediction.place_id }, (place, status) => {
      if (status === 'OK' && place.geometry) {
        updateLocationData(place.geometry.location.lat(), place.geometry.location.lng(), place);
        setSearchQuery(place.formatted_address);
        setSearchResults([]);
      }
    });
  };

  const geocodeAddress = (address) => {
    if (!address || !window.google) return;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        updateLocationData(loc.lat(), loc.lng(), results[0]);
        setSearchQuery(results[0].formatted_address);
        setIsMapVisible(true);
      } else {
        alert('Konum bulunamadı.');
      }
    });
  };

  const findAddressFromCoordinates = (lat, lng) => {
    if (isNaN(lat) || isNaN(lng) || !window.google) return;
    updateLocationData(lat, lng);
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        updateLocationData(lat, lng, results[0]);
        setSearchQuery(results[0].formatted_address);
      }
    });
  };

  const updateLocationData = (lat, lng, placeData = null) => {
    setSelectedLocation({ lat, lng });
    setViewport({ center: { lat, lng }, zoom: 14 });

    const newData = { ...formData, latitude: lat.toString(), longitude: lng.toString() };

    if (placeData) {
       const comps = placeData.address_components;
       newData.city = comps.find(c => c.types.includes('administrative_area_level_1'))?.long_name || '';
       newData.country = comps.find(c => c.types.includes('country'))?.short_name || '';
       
       if (recordType === 'address' || recordType === 'company') {
           newData.description = placeData.formatted_address;
       }
       if (recordType === 'address') {
           newData.state = comps.find(c => c.types.includes('administrative_area_level_2'))?.long_name || ''; 
       }
    }
    setFormData(newData);
  };
  
  const getFilteredData = () => {
    const q = tableSearchQuery.toLowerCase();
    if (activeTab === 0) {
        return addresses.filter(item => 
            item.name?.toLowerCase().includes(q) || 
            item.city?.toLowerCase().includes(q)
        );
    } else if (activeTab === 1) {
        return customs.filter(item => 
            item.customs_name?.toLowerCase().includes(q) || 
            item.customs_code?.toLowerCase().includes(q)
        );
    } else {
        return companies.filter(item => 
            item.name?.toLowerCase().includes(q) || 
            item.contact_person?.toLowerCase().includes(q)
        );
    }
  };

  const filteredData = getFilteredData();
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-100 h-screen">
       {/* Header */}
       <header className="bg-white shadow z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button className="text-gray-500 lg:hidden mr-4">
                <FontAwesomeIcon icon={faBars} className="text-xl" />
              </button>
            </div>
            <button onClick={handleLogout} className="flex items-center space-x-2 text-gray-700">
              <span>{user?.email}</span>
              <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-x-hidden overflow-y-auto">
        <div className="container mx-auto px-6 py-8">
            
            {/* Üst Kontroller ve Tablar */}
            <div className="bg-white rounded-lg shadow mb-6 p-4">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                    <Tabs 
                        value={activeTab} 
                        onChange={(e, v) => { setActiveTab(v); setCurrentPage(1); setTableSearchQuery(''); }}
                        textColor="primary"
                        indicatorColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        <Tab label="Adresler" />
                        <Tab label="Gümrükler" />
                        <Tab label="Firmalar" />
                    </Tabs>

                    <button
                        onClick={() => handleOpenModal(
                            activeTab === 0 ? 'address' : activeTab === 1 ? 'customs' : 'company'
                        )}
                        className="bg-[#4263EB] text-white px-6 py-2 rounded-md hover:bg-[#3b5bd9] flex items-center transition-colors whitespace-nowrap"
                    >
                        <AddIcon className="mr-2" />
                        Yeni Ekle
                    </button>
                </div>
            </div>

            {/* Arama Kutusu (Ortak) */}
            <div className="bg-white rounded-t-lg border-b p-4">
                <TextField
                    fullWidth
                    placeholder={
                        activeTab === 0 ? "Adreslerde ara..." : 
                        activeTab === 1 ? "Gümrüklerde ara..." : 
                        "Firmalarda ara..."
                    }
                    variant="outlined"
                    size="small"
                    value={tableSearchQuery}
                    onChange={(e) => { setTableSearchQuery(e.target.value); setCurrentPage(1); }}
                />
            </div>

            {/* TABLOSAL GÖRÜNÜM */}
            <div className="bg-white rounded-b-lg shadow overflow-hidden overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {activeTab === 0 && (
                                <>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Adres Adı</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Açık Adres</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lokasyon</th>
                                </>
                            )}
                            {activeTab === 1 && (
                                <>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kod</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gümrük Adı</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lokasyon</th>
                                </>
                            )}
                            {activeTab === 2 && (
                                <>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Firma Adı</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">İletişim</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tip</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durum</th>
                                </>
                            )}
                             <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {currentItems.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                                {/* ADRESLER SATIRLARI */}
                                {activeTab === 0 && (
                                    <>
                                        <td className="px-6 py-4 font-medium text-gray-900">{item.name}</td>
                                        <td className="px-6 py-4 text-gray-500 text-sm truncate max-w-xs" title={item.description}>{item.description}</td>
                                        <td className="px-6 py-4 text-gray-500 text-sm">{item.city} / {item.country}</td>
                                    </>
                                )}

                                {/* GÜMRÜKLER SATIRLARI */}
                                {activeTab === 1 && (
                                    <>
                                        <td className="px-6 py-4 font-medium text-gray-900">{item.customs_code}</td>
                                        <td className="px-6 py-4 text-gray-900">{item.customs_name}</td>
                                        <td className="px-6 py-4 text-gray-500">{item.city} / {item.country}</td>
                                    </>
                                )}

                                {/* FİRMALAR SATIRLARI */}
                                {activeTab === 2 && (
                                    <>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{item.name}</div>
                                            <div className="text-xs text-gray-400">{item.city}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            <div>{item.contact_person}</div>
                                            <div className="text-gray-500">{item.phone}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                {item.company_type === 'both' ? 'Her İkisi' : item.company_type === 'sender' ? 'Gönderici' : 'Alıcı'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4">
                                             <span className={`px-2 py-1 text-xs font-semibold rounded-full ${item.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {item.active ? 'Aktif' : 'Pasif'}
                                            </span>
                                        </td>
                                    </>
                                )}

                                <td className="px-6 py-4 text-right whitespace-nowrap">
                                    <IconButton 
                                        onClick={() => handleOpenModal(activeTab === 0 ? 'address' : activeTab === 1 ? 'customs' : 'company', item)} 
                                        color="primary" 
                                        size="small"
                                    >
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton 
                                        onClick={() => confirmDelete(item.id, activeTab === 0 ? 'address' : activeTab === 1 ? 'customs' : 'company')} 
                                        color="error" 
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </td>
                            </tr>
                        ))}
                        {currentItems.length === 0 && (
                            <tr><td colSpan={6} className="px-6 py-4 text-center text-gray-500">Kayıt bulunamadı.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-4 py-3 flex items-center justify-between border-t border-gray-200 rounded-b-lg">
                <div className="flex items-center">
                    <span className="text-sm text-gray-700 mr-2">Sayfa başı:</span>
                    <select 
                        value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))}
                        className="border border-gray-300 rounded text-sm p-1"
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                    </select>
                </div>
                <div className="flex gap-2">
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 border rounded bg-white disabled:opacity-50">
                        <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                        <span className="px-3 py-1 text-sm pt-2">Sayfa {currentPage} / {totalPages || 1}</span>
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border rounded bg-white disabled:opacity-50">
                        <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                </div>
            </div>

        </div>
      </main>



      <Modal open={openModal} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
            {/* Modal Header */}
            <Typography variant="h6" component="h2" mb={2}>
                {editingId ? 'Kaydı Düzenle' : 'Yeni Kayıt Ekle'}
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 3, width: '100%', p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend">Kayıt Tipi</FormLabel>
                <RadioGroup
                    row
                    value={recordType}
                    onChange={(e) => {
                        setRecordType(e.target.value);
                    }}
                >
                    <FormControlLabel value="address" control={<Radio />} label="Normal Adres" />
                    <FormControlLabel value="customs" control={<Radio />} label="Gümrük" />
                    <FormControlLabel value="company" control={<Radio />} label="Firma" />
                </RadioGroup>
            </FormControl>

            <div className="space-y-4">
                
                {/* 1. SEÇENEK: ADRES ÖZEL ALANLARI */}
                {recordType === 'address' && (
                    <TextField 
                        label="Adres Tanımı (Örn: Merkez Depo)" 
                        fullWidth size="small"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                    />
                )}

                {/* 2. SEÇENEK: GÜMRÜK ÖZEL ALANLARI */}
                {recordType === 'customs' && (
                    <div className="grid grid-cols-2 gap-4">
                        <TextField 
                            label="Gümrük Kodu" 
                            fullWidth size="small"
                            value={formData.customs_code}
                            onChange={(e) => setFormData({...formData, customs_code: e.target.value})}
                        />
                        <TextField 
                            label="Gümrük Adı" 
                            fullWidth size="small"
                            value={formData.customs_name}
                            onChange={(e) => setFormData({...formData, customs_name: e.target.value})}
                        />
                    </div>
                )}

                {/* 3. SEÇENEK: FİRMA ÖZEL ALANLARI */}
                {recordType === 'company' && (
                    <>
                        <div className="grid grid-cols-2 gap-4">
                            <TextField 
                                label="Firma Adı" required
                                fullWidth size="small"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                            />
                            <TextField 
                                label="İletişim Kişisi" 
                                fullWidth size="small"
                                value={formData.contact_person}
                                onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <TextField 
                                label="E-posta" 
                                fullWidth size="small"
                                value={formData.email}
                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                            />
                            <TextField 
                                label="Telefon" 
                                fullWidth size="small"
                                value={formData.phone}
                                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                             <TextField 
                                label="Vergi No" 
                                fullWidth size="small"
                                value={formData.tax_number}
                                onChange={(e) => setFormData({...formData, tax_number: e.target.value})}
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel>Firma Tipi</InputLabel>
                                <Select
                                    value={formData.company_type}
                                    label="Firma Tipi"
                                    onChange={(e) => setFormData({...formData, company_type: e.target.value})}
                                >
                                    <MenuItem value="sender">Gönderici</MenuItem>
                                    <MenuItem value="receiver">Alıcı</MenuItem>
                                    <MenuItem value="both">Her İkisi</MenuItem>
                                </Select>
                            </FormControl>
                        </div>
                    </>
                )}

                <div className="relative">
                    <TextField 
                        label={recordType === 'company' ? "Firma Adresi" : "Açık Adres / Konum Ara"} 
                        placeholder="Haritada bulmak için adres yazın..."
                        fullWidth size="small"
                        multiline={recordType === 'company' || recordType === 'address'}
                        rows={recordType === 'company' ? 2 : 1}
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setFormData(prev => ({...prev, description: e.target.value}));
                        }}
                    />
                    {searchResults.length > 0 && (
                        <div className="absolute z-50 w-full bg-white border border-gray-300 rounded shadow-lg max-h-40 overflow-y-auto mt-1">
                            {searchResults.map((res) => (
                                <div key={res.place_id} onClick={() => handlePlaceSelect(res)} className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm">
                                    {res.description}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex gap-2">
                    <Button variant="contained" onClick={() => geocodeAddress(searchQuery)} fullWidth size="small">
                        Haritada Bul
                    </Button>
                    <Button variant="outlined" onClick={() => setIsMapVisible(!isMapVisible)} fullWidth size="small">
                        {isMapVisible ? 'Haritayı Gizle' : 'Haritada Göster'}
                    </Button>
                </div>

                {/* Harita */}
                {isMapVisible && (
                    <div className="h-[200px] w-full border rounded overflow-hidden relative">
                         <APIProvider apiKey={'AIzaSyDB8KdSyduBU2vadQbJhojnwu5gNsj2f-o'} libraries={['places']}>
                            <Map
                                zoom={viewport.zoom}
                                center={viewport.center}
                                gestureHandling="greedy"
                                disableDefaultUI={false}
                                style={{ width: '100%', height: '100%' }}
                                onCameraChanged={(ev) => setViewport({ center: ev.detail.center, zoom: ev.detail.zoom })}
                                onClick={(ev) => {
                                    if(ev.detail.latLng) {
                                        findAddressFromCoordinates(ev.detail.latLng.lat, ev.detail.latLng.lng);
                                    }
                                }}
                            >
                                {selectedLocation && <Marker position={selectedLocation} />}
                            </Map>
                        </APIProvider>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                    <TextField label="Şehir" size="small" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                    <TextField label="Ülke" size="small" value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} />
                </div>
                
                {recordType === 'address' && (
                     <TextField label="İlçe" fullWidth size="small" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} />
                )}

                {recordType === 'company' && (
                    <>
                        <TextField 
                            label="Notlar" fullWidth multiline rows={2} size="small"
                            value={formData.notes}
                            onChange={(e) => setFormData({...formData, notes: e.target.value})}
                        />
                         <FormControlLabel
                            control={
                            <Checkbox 
                                checked={formData.active} 
                                onChange={(e) => setFormData({...formData, active: e.target.checked})} 
                            />
                            }
                            label="Aktif Firma"
                        />
                    </>
                )}

                 <div className="flex justify-end gap-3 pt-4 border-t mt-4">
                    <Button onClick={handleCloseModal} color="inherit">İptal</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        {editingId ? 'Güncelle' : 'Kaydet'}
                    </Button>
                 </div>
            </div>
        </Box>
      </Modal>
      
      <Modal open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <Box sx={{ ...modalStyle, width: 400 }}>
            <Typography variant="h6">Silme Onayı</Typography>
            <Typography sx={{ mt: 2 }}>Bu kaydı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</Typography>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button onClick={() => setShowDeleteDialog(false)}>İptal</Button>
                <Button variant="contained" color="error" onClick={handleDelete}>Sil</Button>
            </Box>
        </Box>
      </Modal>

    </div>
  );
};

export default UnifiedAddresses;